import React, {useEffect} from 'react';

import {useCookieConsent} from './CookieConsentProvider';

interface GTMProviderProps {
  /** The children to wrap */
  children: React.ReactNode;
  /** The GTM ID */
  gtmId: string;
}

type DataLayerEventT = {
  /** The event name */
  event: string;
  /** The start time of the event */
  'gtm.start'?: number;
  /** Additional key-value pairs */
  [key: string]: unknown;
};

declare global {
  interface Window {
    dataLayer: DataLayerEventT[];
  }
}

/**
 * GTM Provider
 * @param children - React.ReactNode
 * @param gtmId - string
 * @returns React.FC
 */
export const GTMProvider: React.FC<GTMProviderProps> = ({children, gtmId}) => {
  const {hasAnalyticsConsent, isInitialized} = useCookieConsent();

  useEffect(() => {
    if (!isInitialized || !hasAnalyticsConsent) return;

    // Prevent double-loading
    if (document.getElementById('google-tag-manager')) return;

    // Initialize dataLayer with cross-domain settings
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
      cookie_flags: 'secure;samesite=none',
      cookie_domain: 'auto',
    });

    // Load GTM script
    const script = document.createElement('script');
    script.id = 'google-tag-manager';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;

    // Add error handling
    script.onerror = () => {
      console.error('Failed to load GTM script');
    };

    document.head.insertBefore(script, document.head.firstChild);

    // Add GTM noscript iframe
    const noscript = document.createElement('noscript');
    noscript.id = 'google-tag-manager-noscript';
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);

    return () => {
      // Only remove if they exist
      const scriptElement = document.getElementById('google-tag-manager');
      const noscriptElement = document.getElementById('google-tag-manager-noscript');

      if (scriptElement) document.head.removeChild(scriptElement);
      if (noscriptElement) document.body.removeChild(noscriptElement);
    };
  }, [gtmId, hasAnalyticsConsent, isInitialized]);

  return <>{children}</>;
};
